import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { judgeRange, missingRequirementReason, notChecked, plainResult } from './support.js';

interface AxisVerdict {
  axis: 'roll' | 'yaw' | 'pitch';
  status: ReturnType<typeof judgeRange>['status'];
  value: number;
  limit: number;
  confidence: number;
}

/**
 * Head pose check: tilt (roll), turn (yaw) and chin up/down (pitch).
 *
 * The overlay gets both the measured (possibly tilted) eye line and a perfectly
 * horizontal reference line, which is the clearest way to show a head tilt
 * without a wall of numbers.
 */
export const facePoseCheck: ValidationCheck = {
  id: 'face_pose',
  title: 'Поворот головы',
  description: 'Проверяет roll, yaw и pitch: лицо должно быть анфас, без наклона.',
  kind: 'geometry',
  requires: ['geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['geometry']);
    if (missing) return notChecked(ctx, facePoseCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    if (!geometry) return notChecked(ctx, facePoseCheck, 'Геометрия недоступна.', ctx.rule.severity);

    const limits = {
      roll: numberParam(ctx.rule.params, 'maxRoll', 3),
      yaw: numberParam(ctx.rule.params, 'maxYaw', 5),
      pitch: numberParam(ctx.rule.params, 'maxPitch', 5),
    };
    const poseConfidence = geometry.poseConfidence.value;
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.5);
    /**
     * Roll comes straight from the eye-line landmarks, which are the most
     * accurate points on the mesh, so it deserves far more trust than the
     * yaw/pitch estimates (which are a fallback when no pose matrix exists).
     */
    const confidenceByAxis = {
      roll: 0.85,
      yaw: geometry.poseSource === 'matrix' ? poseConfidence : Math.min(0.45, poseConfidence),
      pitch: geometry.poseSource === 'matrix' ? poseConfidence : Math.min(0.45, poseConfidence),
    } as const;

    const axes: AxisVerdict[] = (
      [
        ['roll', geometry.roll],
        ['yaw', geometry.yaw],
        ['pitch', geometry.pitch],
      ] as const
    ).map(([axis, value]) => {
      const limit = limits[axis];
      const confidence = confidenceByAxis[axis];
      return {
        axis,
        status: judgeRange({
          value: Math.abs(value),
          max: limit,
          warningMargin: 0.2,
          confidence,
          uncertainBelow,
        }).status,
        value,
        limit,
        confidence,
      };
    });

    const worst = axes.reduce<AxisVerdict>(
      (acc, item) => (Math.abs(item.value) / item.limit > Math.abs(acc.value) / acc.limit ? item : acc),
      axes[0] as AxisVerdict,
    );

    const status = axes.some((item) => item.status === 'fail')
      ? 'fail'
      : axes.some((item) => item.status === 'warning')
        ? 'warning'
        : axes.some((item) => item.status === 'uncertain')
          ? 'uncertain'
          : 'pass';

    const describe = (item: AxisVerdict): string => {
      const amount = `${Math.abs(item.value).toFixed(1).replace('.', ',')}°`;
      if (item.axis === 'roll') {
        return `Голова наклонена на ${amount} (допустимо до ${item.limit}°).`;
      }
      if (item.axis === 'yaw') {
        return `Голова повёрнута в сторону на ${amount} (допустимо до ${item.limit}°).`;
      }
      return `Подбородок ${item.value > 0 ? 'поднят' : 'опущен'} на ${amount} (допустимо до ${item.limit}°).`;
    };

    const message =
      status === 'pass'
        ? 'Голова расположена прямо, лицо анфас.'
        : status === 'uncertain'
          ? `Положение головы требует проверки: ${describe(worst)}`
          : describe(worst);

    const rollRadians = (geometry.roll * Math.PI) / 180;
    const halfLength = 0.5;
    const cx = geometry.eyeCenter.x;
    const cy = geometry.eyeCenter.y;
    const dx = Math.cos(rollRadians) * halfLength;
    const dy = Math.sin(rollRadians) * halfLength;

    const shapes: NonNullable<CheckResult['geometry']> = [
      // Measured (tilted) eye line.
      {
        type: 'line',
        coordinates: [
          roundTo(cx - dx, 4),
          roundTo(cy - dy, 4),
          roundTo(cx + dx, 4),
          roundTo(cy + dy, 4),
        ],
      },
      // Horizontal reference at the same height.
      { type: 'line', coordinates: [roundTo(cx - halfLength, 4), roundTo(cy, 4), roundTo(cx + halfLength, 4), roundTo(cy, 4)] },
      { type: 'point', coordinates: [roundTo(cx, 4), roundTo(cy, 4)] },
    ];

    return plainResult({
      id: facePoseCheck.id,
      title: facePoseCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(Math.abs(worst.value), 2),
      max: worst.limit,
      unit: 'deg',
      message,
      requirement: `Наклон не более ${limits.roll}°, поворот не более ${limits.yaw}°, подбородок не более ${limits.pitch}°.`,
      fixability: 'retake',
      geometry: shapes,
      confidence: roundTo(worst.confidence, 2),
      details: {
        roll: geometry.roll,
        yaw: geometry.yaw,
        pitch: geometry.pitch,
        poseSource: geometry.poseSource,
        poseConfidence: geometry.poseConfidence,
        axisConfidence: confidenceByAxis,
        limits,
        displayValue: `${Math.abs(worst.value).toFixed(1).replace('.', ',')}°`,
      },
    });
  },
};
